import React from 'react';
// EditModeProvider is kept even though edit UI is fully commented out in Header.
// Components still consume useEditMode() for conditional dashed-border styling —
// removing the provider would cause a context crash.
import { EditModeProvider } from '@/context/EditModeContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Header } from '@/components/report/Header';
import { HeroSection } from '@/components/report/HeroSection';
import { AssessmentSummary } from '@/components/report/AssessmentSummary';
import { KeyInfoSection } from '@/components/report/KeyInfoSection';
import { RTOVerificationSection } from '@/components/report/RTOVerificationSection';
import { OBDReportMobile } from '@/components/report/OBDReportMobile';
import { InspectionDetailsSection } from '@/components/report/InspectionDetailsSection';
import { VehicleDetailsSection } from '@/components/report/VehicleDetailsSection';
import { Footer } from '@/components/report/Footer';
// Static dummy data — PII fields (name, phone, reg number, engine/chassis no,
// financier name) are masked as XXXXXXXX. No API call needed for this page.
import { sampleReportData, sampleReportDataBM } from '@/data/sampleReportData';

function SampleReportContent() {
  const { isBahasaMalaysia } = useCurrency();
  const data = isBahasaMalaysia ? sampleReportDataBM : sampleReportData;

  return (
    // .report-page scopes all CSS custom properties (--primary, --accent, etc.)
    // to WiseDrive Navy/Teal brand colors via index.css, without touching the
    // rest of the website's global theme (which uses black/green).
    <div className="report-page min-h-screen">
      {/* Sticky header — share + print only; edit/save/publish commented out */}
      <Header data={data.header} />

      {/* max-w-7xl centers content on large screens; sections handle their own px */}
      <main className="max-w-7xl mx-auto pb-8">
        {/* Hero: Navy left panel (vehicle info + market value) + white right panel (rating circle + recommendation) */}
        <HeroSection
          header={data.header}
          vehicleInfo={data.vehicleInfo}
        />

        {/* AI paragraph + color-coded key highlights (critical / warning / info) */}
        <AssessmentSummary data={data.assessmentSummary} />

        {/* Collapsible — primary 5 fields always visible, 7 sensitive fields behind "Show more" */}
        <VehicleDetailsSection data={data.vehicleInfo} />

        {/* 10-item grid; Insurance / Tyre / Minor Repairs / Major Repairs open detail modals */}
        <KeyInfoSection data={data.keyInfo} />

        {/* RTO legal check: challans, hypothecation (financier masked), blacklist, NOC */}
        <RTOVerificationSection data={data.rtoVerification} />

        {/* OBD-2 fault codes grouped by system; critical systems auto-expand */}
        <OBDReportMobile data={data.obdReport} />

        {/* 10 inspection categories; each has horizontally scrollable checkpoint cards */}
        <InspectionDetailsSection data={data.inspectionCategories} />
      </main>

      {/* Navy footer with logo, features/methods lists, address, copyright */}
      <Footer data={data.footer} />
    </div>
  );
}

export function SampleReport() {
  return (
    <EditModeProvider>
      <SampleReportContent />
    </EditModeProvider>
  );
}

export default SampleReport;
